import { useState } from "react";

interface TechniqueData {
  technique_id: string;
  technique_name?: string;
  mastery_level?: string;
  accuracy?: number;
  total_problems?: number;
}

interface EntityTechniqueData {
  entityId: string;
  entityName: string;
  techniques: TechniqueData[];
}

interface TechniqueComparisonTableProps {
  entities: EntityTechniqueData[];
}

const MASTERY_COLORS = {
  expert:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  advanced: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  intermediate:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  beginner:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  novice: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
};

const MASTERY_ORDER = {
  expert: 5,
  advanced: 4,
  intermediate: 3,
  beginner: 2,
  novice: 1,
};

export function TechniqueComparisonTable({
  entities,
}: TechniqueComparisonTableProps) {
  const [sortBy, setSortBy] = useState<"name" | "mastery">("name");

  if (entities.length === 0) {
    return null;
  }

  // Collect all unique techniques across all entities
  const allTechniques = new Map<string, string>();
  entities.forEach((entity) => {
    entity.techniques.forEach((tech) => {
      if (!allTechniques.has(tech.technique_id)) {
        allTechniques.set(
          tech.technique_id,
          tech.technique_name || tech.technique_id,
        );
      }
    });
  });

  // Convert to array and sort
  let techniqueList = Array.from(allTechniques.entries()).map(([id, name]) => ({
    id,
    name,
  }));

  if (sortBy === "mastery") {
    // Sort by highest mastery level across all entities
    techniqueList.sort((a, b) => {
      const aMaxMastery = Math.max(
        ...entities.map((entity) => {
          const tech = entity.techniques.find((t) => t.technique_id === a.id);
          return tech?.mastery_level
            ? MASTERY_ORDER[tech.mastery_level as keyof typeof MASTERY_ORDER] ||
                0
            : 0;
        }),
      );
      const bMaxMastery = Math.max(
        ...entities.map((entity) => {
          const tech = entity.techniques.find((t) => t.technique_id === b.id);
          return tech?.mastery_level
            ? MASTERY_ORDER[tech.mastery_level as keyof typeof MASTERY_ORDER] ||
                0
            : 0;
        }),
      );
      return bMaxMastery - aMaxMastery;
    });
  } else {
    techniqueList.sort((a, b) => a.name.localeCompare(b.name));
  }

  if (techniqueList.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-center text-gray-500 dark:text-gray-400">
          No technique data available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Technique Mastery Comparison
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setSortBy("name")}
            className={`px-3 py-1 text-sm rounded ${
              sortBy === "name"
                ? "bg-indigo-600 text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
            }`}
          >
            Sort by Name
          </button>
          <button
            onClick={() => setSortBy("mastery")}
            className={`px-3 py-1 text-sm rounded ${
              sortBy === "mastery"
                ? "bg-indigo-600 text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
            }`}
          >
            Sort by Mastery
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-900 z-10">
                Technique
              </th>
              {entities.map((entity) => (
                <th
                  key={entity.entityId}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {entity.entityName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {techniqueList.map((technique) => (
              <tr
                key={technique.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800">
                  {technique.name}
                </td>
                {entities.map((entity) => {
                  const techData = entity.techniques.find(
                    (t) => t.technique_id === technique.id,
                  );

                  if (!techData) {
                    return (
                      <td
                        key={entity.entityId}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 dark:text-gray-500"
                      >
                        Not practiced
                      </td>
                    );
                  }

                  const masteryColor = techData.mastery_level
                    ? MASTERY_COLORS[
                        techData.mastery_level as keyof typeof MASTERY_COLORS
                      ] || "bg-gray-100 text-gray-800"
                    : "bg-gray-100 text-gray-800";

                  return (
                    <td
                      key={entity.entityId}
                      className="px-6 py-4 whitespace-nowrap text-sm"
                    >
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${masteryColor}`}
                        >
                          {techData.mastery_level || "N/A"}
                        </span>
                        {techData.accuracy !== undefined && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {techData.accuracy.toFixed(1)}% accuracy
                          </span>
                        )}
                        {techData.total_problems !== undefined && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {techData.total_problems} problems
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
